import { useEffect, useState } from "react"
import ButtonWrap from "../../components/template/ButtonWrap"
import MainTitle from "../../components/template/MainTitle"
import NewInfoFormat from "../../components/template/NewInfoFormat"
import NewInfoSelector from "../../components/template/NewInfoSelector"
import NewInfoIntInput from "../../components/template/NewInfoIntInput"
import NewInfoDescription from "../../components/template/NewInfoDescription"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesDown, faAnglesRight } from '@fortawesome/free-solid-svg-icons'
import api from "../../components/utils/api"
import { PREACTION_OPTIONS } from "./preactionOptions"

const NewInfoCheckbox = ({ value, setValue, elementId, label }) => (
    <div className="mx-2">
        <input id={elementId} checked={value} onChange={() => setValue(!value)} type="checkbox" />
        <label className="mx-2 text-sm" htmlFor={elementId}>{label}</label>
    </div>
)

// A nested field shown one level below a section heading (e.g. under "Class Filter").
const SubField = ({ label, description, children }) => (
    <div>
        <div className="text-left text-sm font-semibold text-gray-600 mb-1">{label}</div>
        <NewInfoDescription contents={[description]} />
        {children}
    </div>
)

const ExperimentNewModal = ({ closeModal, onCreated }) => {
    const [dbOptions, setDbOptions] = useState([])
    const [dbVersion, setDbVersion] = useState()
    const [isDisabled, setIsDisabled] = useState(false)
    const [isAdvanced, setIsAdvanced] = useState(false)
    const [classOptions, setClassOptions] = useState([])
    const [classFilterMode, setClassFilterMode] = useState("exclude") // "exclude" | "include"
    const [excludedClasses, setExcludedClasses] = useState([])
    const [includedClasses, setIncludedClasses] = useState([])
    const [includeDefaultFilter, setIncludeDefaultFilter] = useState(true)
    const [minSample, setMinSample] = useState(PREACTION_OPTIONS.min_samples.defaultValue)
    const [maxSample, setMaxSample] = useState(PREACTION_OPTIONS.max_samples.defaultValue)
    const [errorMessage, setErrorMessage] = useState("")
    useEffect(() => {
        const fetchDbs = async () => {
            try {
                const res = await api.get('/api/dbs')
                setDbOptions([...res.data.dbs.map(e => ({ label: e.db_ver, value: e.id }))])
            } catch (error) {
                console.error(error)
            }
        }
        fetchDbs()
    }, [])

    useEffect(() => {
        setIsDisabled(!dbVersion)
        // load the selected DB's event classes so the user can pick which to exclude/include
        setExcludedClasses([])
        setIncludedClasses([])
        if (!dbVersion) {
            setClassOptions([])
            return
        }
        const fetchEventClasses = async () => {
            try {
                const res = await api.get(`/api/dbs/${dbVersion.value}/event-classes`)
                setClassOptions(res.data.event_classes.map(name => ({ label: name, value: name })))
            } catch (error) {
                console.error(error)
            }
        }
        fetchEventClasses()
    }, [dbVersion])

    const onSubmit = async () => {
        setErrorMessage("")
        // only honor the advanced options when the section is expanded, so a
        // collapsed (default) state submits without any custom exclusions
        let body = { db_id: dbVersion.value }
        if (isAdvanced) {
            const min = Number(minSample)
            const max = Number(maxSample)
            // validate up front so the user gets immediate feedback instead of a bare 400
            if (!Number.isInteger(min) || min < 1 || !Number.isInteger(max) || max < 1) {
                setErrorMessage("Minimum and maximum samples must be positive whole numbers.")
                return
            }
            if (min > max) {
                setErrorMessage("Minimum samples must be less than or equal to maximum samples.")
                return
            }
            // an empty include list would otherwise be sent as [] and silently fall back
            // to exclude mode on the backend, which is not what the user picked
            if (classFilterMode === "include" && includedClasses.length === 0) {
                setErrorMessage("Select at least one class to include, or switch to Exclude.")
                return
            }
            // the radio keeps the two modes mutually exclusive, so only the active
            // mode's class selection is sent
            const classFilter = classFilterMode === "include"
                ? { included_classes: includedClasses.map(e => e.value) }
                : { excluded_classes: excludedClasses.map(e => e.value), include_default_filter: includeDefaultFilter }
            body = {
                ...body,
                ...classFilter,
                min_sample: min,
                max_sample: max,
            }
        }
        try {
            await api.post(`/api/experiments?advanced=false`, body)
            onCreated()
            closeModal()
        } catch (error) {
            console.error(error)
            setErrorMessage(error.response?.data?.message || "Failed to create the experiment. Please try again.")
        }
    }

    return (
        <div className='h-full'>
            <MainTitle title="New Experiment" />
            <div>
                {[
                    {
                        label: "Database Version", contents: <NewInfoSelector
                            value={dbVersion}
                            setValue={setDbVersion}
                            options={dbOptions}
                            category=""
                            noOptionsMessage="Loading..."
                        />, description: <NewInfoDescription contents={[
                            "Please choose one database with which you want to run an experiment. ",
                            "If you have permission, you can generate a new database."
                        ]} />
                    },
                ].map((e, i) => <NewInfoFormat label={e.label} contents={e.contents} description={e.description} key={i} />)}
            </div>
            <div className='text-left text-lg text-gray-500'>
                <button type='button' onClick={() => setIsAdvanced(!isAdvanced)}>Advanced Options <FontAwesomeIcon icon={isAdvanced ? faAnglesDown : faAnglesRight} /></button>
            </div>
            {isAdvanced && <div className='md:grid md:grid-cols-2 md:gap-3'>
                {[
                    // sample thresholds come first so they aren't missed below the larger class-filter block
                    {
                        label: PREACTION_OPTIONS.min_samples.label, contents: <NewInfoIntInput value={minSample} setValue={setMinSample} />,
                        description: <NewInfoDescription contents={[PREACTION_OPTIONS.min_samples.description.create]} />
                    },
                    {
                        label: PREACTION_OPTIONS.max_samples.label, contents: <NewInfoIntInput value={maxSample} setValue={setMaxSample} />,
                        description: <NewInfoDescription contents={[PREACTION_OPTIONS.max_samples.description.create]} />
                    },
                    {
                        fullWidth: true,
                        label: "Class Filter",
                        description: <NewInfoDescription contents={[
                            "Exclude selected classes: train every class except the ones you choose (optionally with the default filter).",
                            "Include only selected classes: train only the classes you choose.",
                        ]} />,
                        contents: <div>
                            <div className="text-left flex gap-6">
                                {[["exclude", "Exclude selected classes"], ["include", "Include only selected classes"]].map(([value, label]) => (
                                    <label key={value} className="text-sm flex items-center gap-1">
                                        <input type="radio" name="classFilterMode" checked={classFilterMode === value} onChange={() => setClassFilterMode(value)} />
                                        {label}
                                    </label>
                                ))}
                            </div>
                            <div className="mt-3 pl-4 border-l border-gray-200 space-y-3">
                                {classFilterMode === "include"
                                    ? <SubField label={PREACTION_OPTIONS.included_classes.label} description={PREACTION_OPTIONS.included_classes.description.create}>
                                        <NewInfoSelector
                                            value={includedClasses}
                                            setValue={setIncludedClasses}
                                            options={classOptions}
                                            category=""
                                            isMulti={true}
                                            noOptionsMessage={dbVersion ? "No classes" : "Select a database first"}
                                        />
                                    </SubField>
                                    : <>
                                        <SubField label={PREACTION_OPTIONS.excluded_classes.label} description={PREACTION_OPTIONS.excluded_classes.description.create}>
                                            <NewInfoSelector
                                                value={excludedClasses}
                                                setValue={setExcludedClasses}
                                                options={classOptions}
                                                category=""
                                                isMulti={true}
                                                noOptionsMessage={dbVersion ? "No classes" : "Select a database first"}
                                            />
                                        </SubField>
                                        <SubField label={PREACTION_OPTIONS.default_filter.label} description={PREACTION_OPTIONS.default_filter.description.create}>
                                            <div className="text-left border border-[#CCCCCC] px-4 py-2 rounded flex">
                                                <NewInfoCheckbox value={includeDefaultFilter} setValue={setIncludeDefaultFilter} elementId="checkboxForIncludeDefaultFilter" label={PREACTION_OPTIONS.default_filter.checkboxLabel} />
                                            </div>
                                        </SubField>
                                    </>}
                            </div>
                        </div>
                    },
                ].map((e, i) => <div key={i} className={e.fullWidth ? "md:col-span-2" : ""}><NewInfoFormat label={e.label} contents={e.contents} description={e.description} /></div>)}
            </div>}
            {errorMessage && <div className='mt-4 text-sm text-red-500 text-right'>{errorMessage}</div>}
            <div className='my-4 text-right'>
                <ButtonWrap contents="Run" onClick={onSubmit} isDisabled={isDisabled} />
            </div>
        </div >
    )
}

export default ExperimentNewModal